import { useInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";

import { CustomNodeSearch } from "@/components/CustomNodeSearch";
import { CustomNodeSearchResult } from "@/components/CustomNodeSearchResult";
import { LoadMoreSpinner } from "@/components/LoadMoreSpinner";
import { Page } from "@/components/Page";
import { ButtonLink } from "@/components/ui/button";
import { H1 } from "@/components/ui/typography";
import { searchCustomNodesQueryOptions } from "@/lib/api";

const PAGE_SIZE = 25;

export const Route = createFileRoute(
  "/app/_sidebar-layout/custom-nodes/search",
)({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => {
    // validate and parse the search params into a typed state
    return {
      q: search.q || "",
    } as { q: string };
  },
  loader: async ({ context: { queryClient }, location }) => {
    const search = location.search as { q: string };
    await queryClient.ensureInfiniteQueryData(
      searchCustomNodesQueryOptions({
        query: search.q,
        page: 1,
        page_size: PAGE_SIZE,
      }),
    );
  },
  head: () => ({ meta: [{ title: "Create Custom Node" }] }),
});

function RouteComponent() {
  const { q = "" } = Route.useSearch();
  const { data, fetchNextPage, isLoading } = useInfiniteQuery(
    searchCustomNodesQueryOptions({
      query: q,
      page: 1,
      page_size: PAGE_SIZE,
    }),
  );
  const navigate = useNavigate({ from: "/app/custom-nodes/search" });

  const onSelect = (id: number) =>
    navigate({
      to: "/app/custom-nodes/$nodeId/view",
      params: { nodeId: id.toString() },
    });

  const onSearch = (query: string) =>
    navigate({
      to: "/app/custom-nodes/search",
      search: { q: query },
    });

  const allResults = useMemo(() => {
    return data?.pages.flatMap((d) => d.results) || [];
  }, [data]);

  return (
    <Page>
      <div className="flex items-center justify-between">
        <H1>Custom Nodes</H1>
        <ButtonLink to="/app/custom-nodes/create">Create</ButtonLink>
      </div>
      <CustomNodeSearch
        defaultInputValue={q}
        onSelect={onSelect}
        onSearch={onSearch}
      />

      <div className="flex flex-col gap-4">
        {allResults.map((node) => (
          <Link
            key={node.id}
            to="/app/custom-nodes/$nodeId/view"
            params={{ nodeId: node.id.toString() }}
          >
            <CustomNodeSearchResult item={node} />
          </Link>
        ))}
      </div>

      {data &&
        data.pages[data.pages.length - 1]?.results?.length === PAGE_SIZE && (
          <LoadMoreSpinner
            fetchNextPage={fetchNextPage}
            isLoading={isLoading}
          />
        )}
    </Page>
  );
}
